import { resolvePageRoot } from '@/utils/scrollReveal'

const HERO_ROOT_SELECTOR = '[class*="hero-"], #contacto'
const HERO_MEDIA_FALLBACK_MS = 12000

function waitForFonts() {
  return document.fonts?.ready ?? Promise.resolve()
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve) => {
      window.setTimeout(resolve, ms)
    }),
  ])
}

function loadImageUrl(src) {
  if (!src) return Promise.resolve()
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = src
  })
}

function getBgImageUrl(el) {
  const bg = getComputedStyle(el).backgroundImage
  if (!bg || bg === 'none') return null
  const match = bg.match(/url\(["']?(.*?)["']?\)/)
  return match?.[1] ?? null
}

function collectBgImageUrls(root) {
  const urls = new Set()
  ;[root, ...root.querySelectorAll('*')].forEach((el) => {
    const url = getBgImageUrl(el)
    if (url) urls.add(url)
  })
  return [...urls]
}

function isActiveCarouselMedia(el) {
  const slide = el.closest(
    '.hero-carousel__slide, .equipo-hero-carousel__slide',
  )
  if (!slide) return true
  return slide.classList.contains('hero-carousel__slide--active')
    || slide.classList.contains('equipo-hero-carousel__slide--active')
}

function shouldWaitForImg(img) {
  if (!isActiveCarouselMedia(img)) return false
  const loading = img.getAttribute('loading')
  const fetchPriority = img.getAttribute('fetchpriority')
  if (fetchPriority === 'high' || loading === 'eager') return true
  return !img.closest('[class*="carousel"]')
}

function waitForImgElement(img) {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve()
  return new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true })
    img.addEventListener('error', resolve, { once: true })
  })
}

function getTopLevelHeroes(pageRoot) {
  const all = [...pageRoot.querySelectorAll(HERO_ROOT_SELECTOR)]
  return all.filter(
    (el, _, list) => !list.some((other) => other !== el && other.contains(el)),
  )
}

export async function waitForPageHero(pageRoot) {
  await waitForFonts()

  if (!pageRoot) return

  const heroes = getTopLevelHeroes(pageRoot)
  if (!heroes.length) return

  const tasks = []

  heroes.forEach((hero) => {
    collectBgImageUrls(hero).forEach((url) => {
      tasks.push(loadImageUrl(url))
    })

    hero.querySelectorAll('img').forEach((img) => {
      if (shouldWaitForImg(img)) tasks.push(waitForImgElement(img))
    })
  })

  if (tasks.length) {
    await Promise.all(tasks)
  }
}

export async function waitForCurrentPageHero() {
  const pageRoot = resolvePageRoot(document.querySelector('.page-content'))
  await withTimeout(waitForPageHero(pageRoot), HERO_MEDIA_FALLBACK_MS)
}

export function dismissSiteLoader(store) {
  store.commit('SET_SITE_LOADER', false)
}
